import React, { useContext, useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Button, Grid, Divider, Alert, CircularProgress } from '@mui/material';
import StateContext from '../Contexts/StateContext';
import DispatchContext from '../Contexts/DispatchContext';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import config, { environment } from '../config';
import { useNavigate } from 'react-router-dom';

function Profile() {
    const { t, i18n } = useTranslation();
    const globalState = useContext(StateContext);
    const globalDispatch = useContext(DispatchContext);
    const [userProfile, setUserProfile] = useState(null);
    const [requestStatus, setRequestStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!globalState.userIsLogged || !globalState.userId) {
            setProfileLoading(false);
            return;
        }

        const fetchProfile = async () => {
            try {
                const res = await Axios.get(
                    config[environment].getProfileEndpoint(globalState.userId),
                    { headers: { Authorization: `Bearer ${globalState.jwtAccessToken}` } }
                );
                setUserProfile(res.data);
                globalDispatch({ type: 'catchUserProfileInfo', profile: res.data });
            } catch (e) {
                console.error("Failed to fetch profile in Profile component", e);
                if (e.response && e.response.status === 401) {
                    if (globalState.jwtRefreshToken) {
                        try {
                            const refreshRes = await Axios.post(config[environment].refreshTokenEndpoint, {
                                refresh: globalState.jwtRefreshToken
                            });
                            if (refreshRes.data.access) {
                                globalDispatch({
                                    type: 'catchToken',
                                    tokenValue: refreshRes.data.access,
                                    refreshToken: globalState.jwtRefreshToken
                                });
                                // The useEffect will re-run due to globalState.jwtAccessToken change
                            }
                        } catch (refreshErr) {
                            globalDispatch({ type: 'logout' });
                            navigate(`/${i18n.language}/login`);
                        }
                    } else {
                        globalDispatch({ type: 'logout' });
                        navigate(`/${i18n.language}/login`);
                    }
                }
            } finally {
                setProfileLoading(false);
            }
        };

        fetchProfile();
    }, [globalState.userIsLogged, globalState.userId, globalState.jwtAccessToken, globalState.jwtRefreshToken, globalDispatch, navigate]);

    const handleRequestMembership = async () => {
        setLoading(true);
        try {
            await Axios.post(config[environment].requestMembershipEndpoint, {}, {
                headers: { Authorization: `Bearer ${globalState.jwtAccessToken}` }
            });
            setRequestStatus(t('Profile.MembershipPending'));
        } catch (e) {
            console.error(e);
            setRequestStatus(t('Profile.RequestFailed', 'Hiba történt az igénylés során.'));
        } finally {
            setLoading(false);
        }
    };

    if (profileLoading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!globalState.userIsLogged) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <Typography variant="h5">{t('Profile.PleaseLogin')}</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(`/${i18n.language}/login`)}>
                    {t('Profile.Login')}
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Typography variant="h4" gutterBottom color="primary">
                {t('Profile.Title')}
            </Typography>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">{t('Profile.Data')}</Typography>
                        <Typography>{t('Profile.Username')}: {globalState.userUsername}</Typography>
                        <Typography>{t('Profile.Email')}: {globalState.userEmail}</Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>{t('Profile.Permissions')}</Typography>
                        
                        {requestStatus && (
                            <Alert severity={requestStatus.includes('Hiba') || requestStatus.includes('failed') ? 'error' : 'info'} sx={{ mb: 2 }}>{requestStatus}</Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                            {userProfile?.is_szft_member ? (
                                <Alert severity="success">{t('Profile.AlreadyMember', 'Ön már SZFT tag.')}</Alert>
                            ) : (
                                <Button 
                                    variant="outlined" 
                                    color="primary"
                                    onClick={handleRequestMembership}
                                    disabled={loading}
                                    startIcon={loading && <CircularProgress size={20} />}
                                >
                                    {t('Profile.RequestMembership')}
                                </Button>
                            )}
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        <Alert severity="info">
                            {t('Administration.CrossCompatibilityInfo')}
                        </Alert>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
}

export default Profile;
